import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, X, ChevronDown, ChevronRight, Layers, Users2, Copy, Pencil, Upload, Download, AlertOctagon, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import ConfirmDialog from "../components/ConfirmDialog";

const SYM = { USD: "$", GBP: "£", EUR: "€", INR: "₹" };
const CURRENCIES = ["USD", "GBP", "EUR", "INR"];
const emptyForm = () => ({
  partner_id: "", program_category: "", country: "", city: "", program_name: "",
  tiers: [{ weeks: 1, price: "", currency: "USD" }],
});

// Inline row-level tier editor — reuses the same tier UI as the create form.
function TierEditor({ row, onSave, onCancel }) {
  const [tiers, setTiers] = useState(() => (row.tiers || []).map((t) => ({ ...t })));
  const setTier = (idx, k, v) => setTiers((s) => { const n = [...s]; n[idx] = { ...n[idx], [k]: v }; return n; });
  const addTier = () => setTiers((s) => [...s, { weeks: (s[s.length - 1]?.weeks || 0) + 1, price: "", currency: s[0]?.currency || "USD" }]);
  const removeTier = (idx) => setTiers((s) => s.filter((_, i) => i !== idx));
  const save = () => {
    const clean = tiers
      .map((t) => ({ weeks: parseInt(t.weeks) || 0, price: parseFloat(t.price) || 0, currency: t.currency }))
      .filter((t) => t.weeks > 0);
    if (clean.length === 0) return;
    onSave(clean);
  };
  return (
    <div className="mt-2 space-y-2" data-testid={`pp-tier-editor-${row.id}`}>
      {tiers.map((t, i) => (
        <div key={`et-${i}`} className="grid grid-cols-12 gap-2 items-center">
          <input data-testid={`pp-edit-weeks-${row.id}-${i}`} type="number" min={1} className="input-aii col-span-3" value={t.weeks} onChange={(e) => setTier(i, "weeks", e.target.value)}/>
          <div className="col-span-6 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[color:var(--aii-muted)]">{SYM[t.currency] || t.currency}</span>
            <input data-testid={`pp-edit-price-${row.id}-${i}`} type="number" min={0} step="0.01" className="input-aii pl-8" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)}/>
          </div>
          <select className="input-aii col-span-2" value={t.currency} onChange={(e) => setTier(i, "currency", e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={() => removeTier(i)} disabled={tiers.length === 1}
                  className="col-span-1 text-[color:var(--aii-muted)] hover:text-red-600 disabled:opacity-30 p-1.5 flex justify-center">
            <Trash2 size={13}/>
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2 justify-end">
        <button onClick={addTier} className="text-brand text-xs font-semibold flex items-center gap-1 hover:underline"><Plus size={10}/> Add tier</button>
        <button onClick={onCancel} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-slate-50">Cancel</button>
        <button data-testid={`pp-tier-editor-save-${row.id}`} onClick={save} className="text-xs px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark font-semibold flex items-center gap-1">
          <Save size={10}/> Save tiers
        </button>
      </div>
    </div>
  );
}

export default function PartnerPrograms() {
  const { user } = useAuth();
  const canDelete = (user?.real_role || user?.effective_role) === 'FOUNDER';
  const [config, setConfig] = useState({ categories: [], currencies: CURRENCIES, partners: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [copyOpen, setCopyOpen] = useState(false);
  const [editingTiers, setEditingTiers] = useState(null); // program id currently being tier-edited
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneForm, setCloneForm] = useState({ source_partner_id: "", target_partner_id: "", overwrite: false });
  const [cloning, setCloning] = useState(false);
  // Type-to-confirm state for "delete every program in this destination".
  // Founder-only; kept out of `confirming` so the two flows can't collide.
  const [destDelTarget, setDestDelTarget] = useState(null); // { destination, program_count }
  const [destDelType, setDestDelType] = useState("");
  const [destDelBusy, setDestDelBusy] = useState(false);
  // Curated country list — populated from /api/brand/destinations so the
  // Founder can't freehand-type "Indai" any more. Loaded once on mount.
  const [registry, setRegistry] = useState({ destinations: [], flags: {} });

  const load = async () => {
    setLoading(true);
    try {
      const [c, r, d] = await Promise.all([
        api.get("/partner-programs/config"),
        api.get(`/partner-programs${filterPartner ? `?partner_id=${filterPartner}` : ""}`),
        api.get("/brand/destinations").catch(() => ({ data: { destinations: [] } })),
      ]);
      setConfig(c.data); setRows(r.data); setRegistry({
        destinations: d.data?.destinations || [],
        flags: d.data?.flags || {},
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterPartner]);

  // Group programs by destination first (India / Nepal / Sri Lanka / …)
  // and then by partner within each destination — matches the founder's
  // request "show all India Programs, Nepal Programs, Sri Lanka Programs".
  // Destination bucket key is case-insensitive + trimmed so "India",
  // "INDIA" and " india " all fold into one group. Display label
  // canonicalises to Title Case ("Sri Lanka"). Search filters ALL fields
  // case-insensitively before grouping so a partner typing "medical"
  // finds the right row even in a 500-row catalog.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter(r =>
          [r.program_name, r.country, r.city, r.partner_name, r.program_category]
            .filter(Boolean)
            .some(s => String(s).toLowerCase().includes(q)))
      : rows;
    const canonDest = (s) => {
      const v = (s || "").trim();
      if (!v) return "";
      return v.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
    };
    const byDest = {};
    for (const r of filtered) {
      const canon = canonDest(r.country);
      const key = canon || "Unspecified destination";
      if (!byDest[key]) byDest[key] = { destination: key, partners: {} };
      const pkey = r.partner_id;
      if (!byDest[key].partners[pkey]) {
        byDest[key].partners[pkey] = { partner_id: pkey, partner_name: r.partner_name, programs: [] };
      }
      byDest[key].partners[pkey].programs.push(r);
    }
    return Object.values(byDest)
      .map(d => ({
        ...d,
        // Precomputed flag makes JSX render lookups constant-time.
        // `legacy` = destination isn't in the curated registry, so
        // the "Fix legacy countries" sweep should offer it.
        flag: registry.flags[d.destination] || "",
        legacy: !registry.flags[d.destination] && d.destination !== "Unspecified destination",
        program_count: Object.values(d.partners).reduce((n, p) => n + p.programs.length, 0),
        partners: Object.values(d.partners).sort((a, b) => (a.partner_name || "").localeCompare(b.partner_name || "")),
      }))
      .sort((a, b) => {
        // "Unspecified destination" bucket sinks to the bottom.
        if (a.destination === "Unspecified destination") return 1;
        if (b.destination === "Unspecified destination") return -1;
        return a.destination.localeCompare(b.destination);
      });
  }, [rows, search, registry.flags]);

  // Buckets whose destination isn't in the curated country list.
  // Powers the "Fix legacy countries" nudge + modal.
  const legacyDestinations = useMemo(
    () => grouped.filter(d => d.legacy),
    [grouped]
  );

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const setTier = (idx, k, v) => setForm((f) => {
    const tiers = [...f.tiers];
    tiers[idx] = { ...tiers[idx], [k]: v };
    return { ...f, tiers };
  });
  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, { weeks: (f.tiers[f.tiers.length - 1]?.weeks || 0) + 1, price: "", currency: f.tiers[0]?.currency || "USD" }] }));
  const removeTier = (idx) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.partner_id) return toast.error("Pick a partner");
    if (!form.program_category) return toast.error("Pick a program category");
    if (!form.country) return toast.error("Country required");
    if (!form.program_name) return toast.error("Program name required");
    if (form.tiers.length === 0) return toast.error("Add at least one tier");
    const cleanTiers = form.tiers
      .map((t) => ({ weeks: parseInt(t.weeks) || 0, price: parseFloat(t.price) || 0, currency: t.currency }))
      .filter((t) => t.weeks > 0 && t.price >= 0);
    if (cleanTiers.length === 0) return toast.error("Every tier needs a valid week count");
    try {
      await api.post("/partner-programs", { ...form, tiers: cleanTiers });
      toast.success("Program created");
      setForm(emptyForm()); setShowForm(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doDelete = async (row) => {
    try { await api.delete(`/partner-programs/${row.id}`); toast.success("Program removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doDeleteDestination = async () => {
    if (!destDelTarget) return;
    setDestDelBusy(true);
    try {
      const dest = encodeURIComponent(destDelTarget.destination);
      const { data } = await api.delete(`/partner-programs/destination/${dest}`);
      toast.success(`Wiped ${data.deleted} program${data.deleted === 1 ? "" : "s"} from ${data.country}`);
      setDestDelTarget(null); setDestDelType("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setDestDelBusy(false); }
  };

  const doTemplate = async () => {
    try {
      const { data } = await api.get("/partner-programs/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a");
      a.href = url; a.download = "aii-partner-programs-template.xlsx"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast.error("Could not download template"); }
  };
  const doUpload = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/partner-programs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.programs_written} program(s) imported${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      if (data.errors?.length) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Partner programs import errors:", data.errors);
        }
      }
      // Legacy-country suggestions — offer a one-tap fix modal so the
      // Founder doesn't leave the just-imported catalog with typos.
      if ((data.country_suggestions || []).length > 0) {
        setImportSuggestions(data.country_suggestions);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  // "Did you mean" state from the last bulk import — array of
  // {from, to} pairs surfaced by the backend using difflib on the
  // destination registry. Rendered as a modal after the upload
  // completes so the Founder can apply/skip each pair.
  const [importSuggestions, setImportSuggestions] = useState([]);
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  const applyImportSuggestions = async () => {
    setApplyingSuggestions(true);
    let total = 0;
    try {
      for (const s of importSuggestions) {
        const { data } = await api.post("/partner-programs/rewrite-country", { from: s.from, to: s.to });
        total += data.updated || 0;
      }
      toast.success(`Auto-fixed ${total} program${total === 1 ? "" : "s"} across ${importSuggestions.length} country name${importSuggestions.length === 1 ? "" : "s"}`);
      setImportSuggestions([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fix failed");
    } finally { setApplyingSuggestions(false); }
  };

  const doClone = async () => {
    if (!cloneForm.source_partner_id || !cloneForm.target_partner_id) {
      return toast.error("Pick both a source and a target partner");
    }
    if (cloneForm.source_partner_id === cloneForm.target_partner_id) {
      return toast.error("Source and target must be different partners");
    }
    setCloning(true);
    try {
      const { data } = await api.post("/partner-programs/clone", cloneForm);
      const parts = [
        `${data.cloned} cloned`,
        data.updated > 0 ? `${data.updated} updated` : null,
        data.skipped > 0 ? `${data.skipped} skipped (already exist)` : null,
      ].filter(Boolean).join(" · ");
      if (data.cloned === 0 && data.updated === 0) {
        toast.warning(data.message || parts || "Nothing to clone");
      } else {
        toast.success(`Cloned to ${data.target}: ${parts}`);
      }
      setCloneOpen(false);
      setCloneForm({ source_partner_id: "", target_partner_id: "", overwrite: false });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clone failed"); }
    finally { setCloning(false); }
  };

  // One-tap catalog cleanup — fold every stray "INDIA" / "india " variant
  // into the canonical Title Case form so the destination groupings
  // never split a country across two rows again.
  const [normalizing, setNormalizing] = useState(false);
  // "Fix legacy countries" modal state. Keyed by legacy destination name →
  // the canonical registry country the Founder wants to remap it to.
  // { open: bool, map: { "Indai": "India", ... } }
  const [legacyFix, setLegacyFix] = useState({ open: false, map: {} });
  const [fixingLegacy, setFixingLegacy] = useState(false);
  const doNormalizeCountries = async () => {
    if (!window.confirm("Fold every 'India' / 'INDIA' / stray-cased destination into one canonical Title-Case bucket? Safe to run — bookings stay put.")) return;
    setNormalizing(true);
    try {
      const { data } = await api.post("/partner-programs/normalize-countries");
      if (data.updated === 0) {
        toast.success("Catalog already normalized — nothing to fold.");
      } else {
        toast.success(`Normalized ${data.updated} program${data.updated === 1 ? "" : "s"}. Duplicate destination rows are gone.`);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Normalize failed"); }
    finally { setNormalizing(false); }
  };

  // Walk the legacyDestinations buckets and rewrite each one via
  // /api/partner-programs/rewrite-country. Runs in sequence so a
  // conflict on one row (should never happen but…) doesn't leave the
  // catalog in a half-rewritten state.
  const doFixLegacy = async () => {
    const pairs = Object.entries(legacyFix.map).filter(([, to]) => (to || "").trim());
    if (pairs.length === 0) return toast.warning("Pick a target country for at least one legacy row");
    setFixingLegacy(true);
    let total = 0;
    try {
      for (const [from, to] of pairs) {
        const { data } = await api.post("/partner-programs/rewrite-country", { from, to });
        total += data.updated || 0;
      }
      toast.success(`Fixed ${total} program${total === 1 ? "" : "s"} across ${pairs.length} legacy destination${pairs.length === 1 ? "" : "s"}`);
      setLegacyFix({ open: false, map: {} });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Some rewrites failed — try again");
    } finally { setFixingLegacy(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="partner-programs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
            <Layers size={11}/> Founder Catalog
          </div>
          <h1 className="font-display text-2xl font-semibold">Partner Programs</h1>
          <p className="text-sm text-[color:var(--aii-muted)] max-w-2xl">
            Curate a per-partner catalog of programs (e.g. &ldquo;India Medical Program&rdquo;) with duration-based tiered pricing.
            Each partner can have their own programs and prices — set once, reused whenever a Partner submits a booking.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="pp-template" onClick={doTemplate}
                  className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
            <Download size={11}/> Template
          </button>
          <label data-testid="pp-upload" className="text-xs px-3 py-1.5 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1 cursor-pointer">
            <Upload size={11}/> Bulk import
            <input type="file" accept=".xlsx,.csv,.xls" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])}/>
          </label>
          <button data-testid="pp-clone-open" onClick={() => setCloneOpen(true)}
                  className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"
                  title="Copy every program from one partner over to another">
            <Copy size={11}/> Clone from partner
          </button>
          {canDelete && (
            <button data-testid="pp-normalize" onClick={doNormalizeCountries} disabled={normalizing}
                    className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1 disabled:opacity-50"
                    title="Fold 'India' / 'INDIA' / stray-cased destinations into one canonical bucket">
              {normalizing ? "Normalizing…" : "Normalize destinations"}
            </button>
          )}
          {canDelete && legacyDestinations.length > 0 && (
            <button
              data-testid="pp-fix-legacy"
              onClick={() => setLegacyFix({
                open: true,
                map: Object.fromEntries(legacyDestinations.map(d => [d.destination, ""])),
              })}
              className="text-xs px-3 py-1.5 border border-amber-400 text-amber-800 bg-amber-50 rounded-md hover:bg-amber-100 flex items-center gap-1 font-semibold"
              title={`${legacyDestinations.length} legacy destination${legacyDestinations.length === 1 ? "" : "s"} to fix`}>
              <Wand2 size={11}/> Fix {legacyDestinations.length} legacy
            </button>
          )}
          <select data-testid="pp-filter-partner" value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)}
                  className="input-aii text-sm min-w-[200px]">
            <option value="">All partners</option>
            {config.partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button data-testid="pp-new" onClick={() => { setForm(emptyForm()); setShowForm(true); }}
                  className="px-3 py-2 bg-brand text-white font-semibold rounded-md text-sm flex items-center gap-1 hover:bg-brand-dark">
            <Plus size={14}/> New program
          </button>
        </div>
      </div>

      {/* Catalog search — filters by program name / country / city / partner */}
      <div className="relative">
        <input
          data-testid="pp-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search catalog — program name, country, city, partner…"
          className="w-full pl-9 pr-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm bg-white focus:border-brand focus:outline-none"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--aii-muted)] text-sm">🔍</span>
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] px-2 py-1 text-[color:var(--aii-muted)] hover:text-red-600"
            data-testid="pp-search-clear">
            Clear
          </button>
        )}
      </div>

      {showForm && (
        <div className="aii-card p-5 border-l-4 border-brand" data-testid="pp-form">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-lg">New partner program</h2>
            <button onClick={() => setShowForm(false)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Partner
              <select data-testid="pp-partner" value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })} className="input-aii mt-1">
                <option value="">— select partner —</option>
                {config.partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Program category
              <select data-testid="pp-category" value={form.program_category} onChange={(e) => setForm({ ...form, program_category: e.target.value })} className="input-aii mt-1">
                <option value="">— select category —</option>
                {config.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Destination country
              <select
                data-testid="pp-country"
                value={form.country || ""}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="input-aii mt-1">
                <option value="">— select country —</option>
                {(registry.destinations || []).map(c => (
                  <option key={c.name} value={c.name}>{c.flag} {c.name}</option>
                ))}
                {/* If the row we're editing has a country that isn't in
                    the curated list (legacy typo like "Indai"), keep it
                    selectable so the Founder can see + fix it. */}
                {form.country && !(registry.destinations || []).some(d => d.name === form.country) && (
                  <option value={form.country}>⚠️ {form.country} (legacy)</option>
                )}
              </select>
            </label>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">City
              <input data-testid="pp-city" className="input-aii mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Jaipur / Kathmandu / Hanoi …"/>
            </label>
            <label className="md:col-span-2 text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Program name
              <input data-testid="pp-name" className="input-aii mt-1" value={form.program_name} onChange={(e) => setForm({ ...form, program_name: e.target.value })} placeholder="e.g. India Medical Program"/>
            </label>
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold mb-2 flex items-center justify-between">
              <span>Duration → Price tiers</span>
              <div className="flex items-center gap-2">
                {rows.length > 0 && (
                  <button data-testid="pp-copy-tiers-open" onClick={() => setCopyOpen(true)}
                          className="text-brand text-xs font-semibold flex items-center gap-1 hover:underline">
                    <Copy size={10}/> Copy tiers from…
                  </button>
                )}
                <button data-testid="pp-add-tier" onClick={addTier} className="text-brand text-xs font-semibold flex items-center gap-1 hover:underline"><Plus size={10}/> Add tier</button>
              </div>
            </div>
            <div className="space-y-2">
              {form.tiers.map((t, i) => (
                <div key={`tier-${i}`} data-testid={`pp-tier-${i}`} className="grid grid-cols-12 gap-2 items-center">
                  <input data-testid={`pp-tier-weeks-${i}`} type="number" min={1} className="input-aii col-span-3" value={t.weeks} onChange={(e) => setTier(i, "weeks", e.target.value)} placeholder="Weeks"/>
                  <div className="col-span-6 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[color:var(--aii-muted)]">{SYM[t.currency] || t.currency}</span>
                    <input data-testid={`pp-tier-price-${i}`} type="number" min={0} step="0.01" className="input-aii pl-8" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} placeholder="Price"/>
                  </div>
                  <select data-testid={`pp-tier-currency-${i}`} className="input-aii col-span-2" value={t.currency} onChange={(e) => setTier(i, "currency", e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button data-testid={`pp-tier-remove-${i}`} onClick={() => removeTier(i)} disabled={form.tiers.length === 1}
                          className="col-span-1 text-[color:var(--aii-muted)] hover:text-red-600 disabled:opacity-30 p-1.5 flex justify-center">
                    <Trash2 size={13}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="pp-submit" onClick={submit} className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark flex items-center gap-1">
              <Save size={13}/> Save program
            </button>
          </div>
        </div>
      )}

      {loading && <div className="text-center text-sm text-[color:var(--aii-muted)] py-6">Loading partner catalog…</div>}
      {!loading && grouped.length === 0 && (
        <div className="aii-card p-10 text-center text-sm text-[color:var(--aii-muted)]" data-testid="pp-empty">
          No partner programs yet. Click <b>New program</b> to build the catalog.
        </div>
      )}

      <div className="space-y-3">
        {grouped.map((dg) => (
          <div key={`dest-${dg.destination}`} className="aii-card overflow-hidden" data-testid={`pp-dest-${dg.destination}`}>
            <div className="flex items-stretch">
              <button onClick={() => toggle(`dest-${dg.destination}`)} className="flex-1 text-left p-4 flex items-center justify-between hover:bg-[color:var(--aii-green-tint)]">
                <div className="flex items-center gap-2">
                  {expanded[`dest-${dg.destination}`] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                  <span className="text-xl" data-testid={`pp-dest-flag-${dg.destination}`} title={dg.destination}>
                    {dg.flag || (dg.legacy ? "⚠️" : "📍")}
                  </span>
                  <span className="font-display font-semibold text-base">{dg.destination}</span>
                  {dg.legacy && (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                      legacy
                    </span>
                  )}
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)]">
                    {dg.program_count} program{dg.program_count === 1 ? "" : "s"} · {dg.partners.length} partner{dg.partners.length === 1 ? "" : "s"}
                  </span>
                </div>
              </button>
              {canDelete && dg.destination !== "Unspecified destination" && (
                <button
                  data-testid={`pp-dest-delete-${dg.destination}`}
                  onClick={(e) => { e.stopPropagation(); setDestDelTarget({ destination: dg.destination, program_count: dg.program_count }); setDestDelType(""); }}
                  title={`Wipe every program in ${dg.destination} (${dg.program_count} program${dg.program_count === 1 ? "" : "s"})`}
                  className="px-3 text-[color:var(--aii-muted)] hover:text-red-600 hover:bg-red-50 border-l border-[color:var(--aii-border)] flex items-center">
                  <Trash2 size={14}/>
                </button>
              )}
            </div>
            {expanded[`dest-${dg.destination}`] && (
              <div className="border-t border-[color:var(--aii-border)] divide-y divide-[color:var(--aii-border)]/50">
                {dg.partners.map(g => (
                  <div key={`${dg.destination}-${g.partner_id}`}>
                    <div className="px-4 pt-3 pb-1 flex items-center gap-2 bg-slate-50/50">
                      <Users2 size={12} className="text-brand"/>
                      <span className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{g.partner_name}</span>
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-white border border-[color:var(--aii-border)] text-[color:var(--aii-muted)]">
                        {g.programs.length}
                      </span>
                    </div>
                    {g.programs.map((p) => (
                      <div key={p.id} data-testid={`pp-row-${p.id}`} className="p-4 flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{p.program_name}</span>
                            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-sky-100 text-sky-800">{p.program_category}</span>
                          </div>
                          <div className="text-xs text-[color:var(--aii-muted)] mt-0.5">
                            📍 {p.city ? `${p.city}, ` : ""}{p.country}
                          </div>
                          {editingTiers === p.id ? (
                            <TierEditor row={p} onSave={async (tiers) => {
                              try {
                                await api.patch(`/partner-programs/${p.id}`, { tiers });
                                toast.success("Tiers updated");
                                setEditingTiers(null); await load();
                              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                            }} onCancel={() => setEditingTiers(null)}/>
                          ) : (
                            <div className="mt-2 flex flex-wrap gap-1.5 items-center" data-testid={`pp-tiers-${p.id}`}>
                              {p.tiers.map((t, i) => (
                                <span key={`t-${i}`} className="px-2 py-1 text-xs border border-[color:var(--aii-border)] rounded-md bg-white">
                                  <b>{t.weeks}</b> wk{t.weeks === 1 ? "" : "s"} · <span className="text-brand font-semibold">{SYM[t.currency] || t.currency}{Number(t.price).toLocaleString()}</span>
                                </span>
                              ))}
                              {p.tiers.length === 0 && <span className="text-xs italic text-[color:var(--aii-muted)]">no tiers set</span>}
                              <button
                                data-testid={`pp-edit-tiers-${p.id}`}
                                onClick={() => setEditingTiers(p.id)}
                                className="text-[10px] px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1"
                                title="Update this program's tier prices without recreating the whole row">
                                <Pencil size={10}/> Edit tiers
                              </button>
                            </div>
                          )}
                        </div>
                        {canDelete && (
                          <button data-testid={`pp-delete-${p.id}`}
                                  onClick={() => setConfirming({ row: p })}
                                  className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5 rounded hover:bg-red-50"
                                  title="Remove this program from the catalog">
                            <Trash2 size={13}/>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!confirming}
        title={`Remove "${confirming?.row?.program_name || ""}" from ${confirming?.row?.partner_name || "this partner"}?`}
        description="The program will be hidden from the catalog. Historical bookings are unaffected."
        confirmLabel="Remove"
        onConfirm={() => { doDelete(confirming.row); setConfirming(null); }}
        onCancel={() => setConfirming(null)}
        testid="pp-delete-confirm"
      />

      {importSuggestions.length > 0 && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => !applyingSuggestions && setImportSuggestions([])}>
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="pp-import-suggestions-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] bg-sky-50">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-sky-900">
                💡 Did you mean…
              </h3>
              <p className="text-xs text-sky-900 mt-1">
                Your bulk import used {importSuggestions.length} country name{importSuggestions.length === 1 ? "" : "s"}
                that aren&apos;t in the curated registry. Here&apos;s the closest match for each — apply all in one tap
                or skip if these are on purpose.
              </p>
            </div>
            <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
              {importSuggestions.map((s, i) => (
                <div key={`imp-${i}`} className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 flex-1">⚠️ {s.from}</span>
                  <span className="text-slate-400">→</span>
                  <span className="text-emerald-700 font-semibold flex-1">{s.to}</span>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setImportSuggestions([])} disabled={applyingSuggestions}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Skip
              </button>
              <button
                data-testid="pp-import-suggestions-apply"
                onClick={applyImportSuggestions}
                disabled={applyingSuggestions}
                className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50">
                {applyingSuggestions ? "Applying…" : "Apply all"}
              </button>
            </div>
          </div>
        </div>
      )}

      {legacyFix.open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => !fixingLegacy && setLegacyFix({ open: false, map: {} })}>
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden flex flex-col max-h-[85vh]"
               onClick={e => e.stopPropagation()} data-testid="pp-legacy-fix-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] bg-amber-50">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-amber-900">
                <Wand2 size={16} className="text-amber-700"/> Fix {legacyDestinations.length} legacy destination{legacyDestinations.length === 1 ? "" : "s"}
              </h3>
              <p className="text-xs text-amber-900 mt-1">
                Every row below has a country that isn&apos;t in the curated registry (usually a typo like &ldquo;Indai&rdquo;
                or a stray-cased &ldquo;INDIA&rdquo;). Pick the correct destination for each — leave blank to keep it as-is.
              </p>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {legacyDestinations.map(d => (
                <div key={d.destination} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0"
                     data-testid={`pp-legacy-row-${d.destination}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      <span className="text-lg">⚠️</span> {d.destination}
                    </div>
                    <div className="text-[11px] text-[color:var(--aii-muted)]">
                      {d.program_count} program{d.program_count === 1 ? "" : "s"} · {d.partners.length} partner{d.partners.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-slate-400 text-sm">→</div>
                  <select
                    data-testid={`pp-legacy-target-${d.destination}`}
                    value={legacyFix.map[d.destination] || ""}
                    onChange={e => setLegacyFix(s => ({ ...s, map: { ...s.map, [d.destination]: e.target.value } }))}
                    className="text-sm px-2 py-1.5 border border-slate-300 rounded-md bg-white min-w-[180px]">
                    <option value="">— skip —</option>
                    {(registry.destinations || []).map(c => (
                      <option key={c.name} value={c.name}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setLegacyFix({ open: false, map: {} })} disabled={fixingLegacy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                data-testid="pp-legacy-fix-submit"
                onClick={doFixLegacy}
                disabled={fixingLegacy || Object.values(legacyFix.map).filter(v => (v || "").trim()).length === 0}
                className="px-4 py-2 text-sm bg-amber-600 text-white font-semibold rounded-md hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1">
                <Wand2 size={13}/> {fixingLegacy ? "Fixing…" : "Apply fixes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {destDelTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => !destDelBusy && setDestDelTarget(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="pp-dest-delete-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] bg-red-50">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-red-800">
                <AlertOctagon size={18} className="text-red-600"/> Wipe every program in {destDelTarget.destination}?
              </h3>
              <p className="text-xs text-red-800 mt-1">
                This soft-deletes all <b>{destDelTarget.program_count}</b> program{destDelTarget.program_count === 1 ? "" : "s"}
                {" "}across every partner in <b>{destDelTarget.destination}</b>. Historical bookings that already
                reference these programs are unaffected. You can rebuild the catalog via bulk import.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <label className="text-xs font-semibold text-[color:var(--aii-text-1)] block">
                To confirm, type <code className="text-red-700 bg-red-50 px-1 rounded">WIPE {destDelTarget.destination}</code>
              </label>
              <input
                data-testid="pp-dest-delete-input"
                value={destDelType}
                onChange={e => setDestDelType(e.target.value)}
                autoFocus
                placeholder={`WIPE ${destDelTarget.destination}`}
                className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm font-mono"/>
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setDestDelTarget(null)} disabled={destDelBusy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                data-testid="pp-dest-delete-submit"
                onClick={doDeleteDestination}
                disabled={destDelBusy || destDelType.trim() !== `WIPE ${destDelTarget.destination}`}
                className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                <Trash2 size={13}/> {destDelBusy ? "Wiping…" : `Delete ${destDelTarget.program_count} program${destDelTarget.program_count === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {copyOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCopyOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pp-copy-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold">Copy tiers from another program</h3>
                <p className="text-xs text-[color:var(--aii-muted)]">Pick any existing program — its tiers will replace the ones you&apos;re editing.</p>
              </div>
              <button onClick={() => setCopyOpen(false)} className="text-[color:var(--aii-muted)] hover:text-red-600"><X size={16}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5" data-testid="pp-copy-list">
              {rows.length === 0 && <div className="text-sm italic text-[color:var(--aii-muted)] p-4 text-center">No programs to copy from yet.</div>}
              {rows.map((r) => (
                <button
                  key={r.id}
                  data-testid={`pp-copy-source-${r.id}`}
                  type="button"
                  onClick={() => {
                    setForm((f) => ({
                      ...f,
                      tiers: (r.tiers || []).map((t) => ({ weeks: t.weeks, price: t.price, currency: t.currency })),
                    }));
                    setCopyOpen(false);
                    toast.success(`Copied ${r.tiers?.length || 0} tier(s) from ${r.program_name}`);
                  }}
                  className="w-full text-left border border-[color:var(--aii-border)] rounded-md p-2.5 hover:border-brand hover:bg-[color:var(--aii-green-tint)]"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="font-semibold text-sm">{r.program_name}</div>
                      <div className="text-[11px] text-[color:var(--aii-muted)]">{r.partner_name} · {r.program_category} · {r.city ? `${r.city}, ` : ""}{r.country}</div>
                    </div>
                    <span className="text-[10px] text-[color:var(--aii-muted)]">{r.tiers?.length || 0} tier{r.tiers?.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(r.tiers || []).map((t, i) => (
                      <span key={`ct-${r.id}-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-[color:var(--aii-border)]">
                        {t.weeks}wk · {SYM[t.currency] || t.currency}{Number(t.price).toLocaleString()}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {cloneOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCloneOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pp-clone-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Copy size={16} className="text-brand"/> Clone all programs</h3>
                <p className="text-xs text-[color:var(--aii-muted)]">Every active program on the <b>source</b> partner is copied over to the <b>target</b>. Programs already on the target with the same name+location are skipped by default.</p>
              </div>
              <button onClick={() => setCloneOpen(false)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
            </div>
            <div className="p-4 space-y-3">
              <label className="block text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">From partner
                <select data-testid="pp-clone-source" value={cloneForm.source_partner_id}
                        onChange={(e) => setCloneForm({ ...cloneForm, source_partner_id: e.target.value })} className="input-aii mt-1">
                  <option value="">— pick source partner —</option>
                  {config.partners.map((p) => <option key={`cs-${p.id}`} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="block text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">To partner
                <select data-testid="pp-clone-target" value={cloneForm.target_partner_id}
                        onChange={(e) => setCloneForm({ ...cloneForm, target_partner_id: e.target.value })} className="input-aii mt-1">
                  <option value="">— pick target partner —</option>
                  {config.partners.filter((p) => p.id !== cloneForm.source_partner_id).map((p) => <option key={`ct-${p.id}`} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-[color:var(--aii-text-2)]">
                <input data-testid="pp-clone-overwrite" type="checkbox" checked={cloneForm.overwrite}
                       onChange={(e) => setCloneForm({ ...cloneForm, overwrite: e.target.checked })}/>
                Overwrite target&apos;s existing tier prices when a matching program already exists
              </label>
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setCloneOpen(false)} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="pp-clone-submit" onClick={doClone} disabled={cloning}
                      className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
                <Copy size={13}/> {cloning ? "Cloning…" : "Clone programs"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
